import {
  Box,
  Button,
  Modal,
  Typography,
  Grid,
  CircularProgress,
} from "@mui/material";
import { useRef, useState } from "react";
import { toast } from "react-toastify";
import { importPmjayBulk, getPmjayImportLogs } from 'api/pmjay';

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "55%",
  bgcolor: "background.paper",
  borderRadius: "15px",
  boxShadow: 24,
  p: 4,
};

const PmjayImportModal = ({ open, onClose }) => {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [logs, setLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (!selected) return;
    const ext = selected.name.split(".").pop().toLowerCase();
    if (!["xlsx", "csv"].includes(ext)) {
      toast.error("Sirf .xlsx ya .csv file allowed hai!");
      return;
    }
    setFile(selected);
    setImportResult(null);
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("Pehle file select karo!");
      return;
    }
    const formData = new FormData();
    formData.append("pmjay", file);
    setIsUploading(true);
    try {
      const res = await importPmjayBulk(formData);
      setImportResult(res?.data?.data);
      toast.success(res?.data?.message || "Import successful!");
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) {
      toast.error(err?.response?.data?.message || "Import failed!");
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownloadTemplate = () => {
    const headers = [
      "Specialty",
      "Specialty Code",
      "Package Code HBP 2022",
      "AB PMJAY Package Name",
      "Procedure code HBP 2022",
      "Procedure Name 2022",
      "Tier3 (Z)",
      "Tier2 (Y)",
      "Tier1 (X)",
      "Implant mapped.",
      "implant cost",
      "Stratification remarks",
    ];
    const csvContent =
      headers.join(",") +
      "\n" +
      "Cardiology,CD,P001,Sample Package,PR001,Sample Procedure,5000,6000,7000,yes,2000,Sample Remark";
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "pmjay_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const loadLogs = async () => {
    setLogsLoading(true);
    setShowLogs(true);
    try {
      const res = await getPmjayImportLogs({ page: 1, limit: 10 });
      setLogs(res?.data?.data?.logs || []);
    } catch {
      toast.error("Failed to Fetch Logs!");
    } finally {
      setLogsLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        onClose();
        setImportResult(null);
        setShowLogs(false);
      }}
    >
      <Box sx={modalStyle}>
        {/* Header */}
        <Typography variant="h5" sx={{ mb: 3 }}>
          Bulk Import PMJAY Packages
        </Typography>

        {/* Upload Section */}
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.csv"
              onChange={handleFileChange}
              style={{ display: "none" }}
              id="pmjay-file-input"
            />
            <label htmlFor="pmjay-file-input">
              <Button
                variant="outlined"
                component="span"
                fullWidth
                sx={{ borderColor: "#194AF5", color: "#194AF5" }}
              >
                {file ? file.name : "Choose File (.xlsx / .csv)"}
              </Button>
            </label>
          </Grid>

          <Grid item xs={12} sm={3}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleUpload}
              disabled={!file || isUploading}
              sx={{ boxShadow: "none" }}
            >
              {isUploading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Upload"
              )}
            </Button>
          </Grid>

          <Grid item xs={12} sm={3}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleDownloadTemplate}
              sx={{ borderColor: "#194AF5", color: "#194AF5" }}
            >
              Download Template
            </Button>
          </Grid>
        </Grid>

        {/* Import Result */}
        {importResult && (
          <Box
            sx={{
              mt: 3,
              p: 2,
              bgcolor: "#F8F9FF",
              borderRadius: "10px",
              border: "1px solid #E0E4FF",
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
              Import Result
            </Typography>
            <Grid container spacing={1}>
              {[
                {
                  label: "Total Records",
                  value: importResult.totalRecords,
                  color: "#555",
                },
                {
                  label: "Success",
                  value: importResult.successRecords,
                  color: "green",
                },
                {
                  label: "Failed",
                  value: importResult.failedRecords,
                  color: "red",
                },
                {
                  label: "Status",
                  value: importResult.importStatus,
                  color:
                    importResult.importStatus === "SUCCESS"
                      ? "green"
                      : importResult.importStatus === "PARTIAL"
                        ? "orange"
                        : "red",
                },
              ].map((item) => (
                <Grid item xs={6} sm={3} key={item.label}>
                  <Box
                    sx={{
                      textAlign: "center",
                      p: 1,
                      bgcolor: "white",
                      borderRadius: "8px",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {item.label}
                    </Typography>
                    <Typography
                      variant="h6"
                      color={item.color}
                      fontWeight="bold"
                    >
                      {item.value}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>

            {/* Failed Rows */}
            {importResult.failedRows?.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" color="error" sx={{ mb: 1 }}>
                  Failed Rows:
                </Typography>
                <Box sx={{ maxHeight: "150px", overflowY: "auto" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: "13px",
                    }}
                  >
                    <thead>
                      <tr style={{ background: "#FFE6E6" }}>
                        <th style={{ padding: "6px", textAlign: "left" }}>
                          Row #
                        </th>
                        <th style={{ padding: "6px", textAlign: "left" }}>
                          Package Code
                        </th>
                        <th style={{ padding: "6px", textAlign: "left" }}>
                          Reason
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {importResult.failedRows.map((row, i) => (
                        <tr
                          key={i}
                          style={{ borderBottom: "1px solid #f0f0f0" }}
                        >
                          <td style={{ padding: "6px" }}>{row.rowNumber}</td>
                          <td style={{ padding: "6px" }}>{row.packageCode}</td>
                          <td style={{ padding: "6px", color: "red" }}>
                            {row.reason}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* Import Logs */}
        <Box sx={{ mt: 3 }}>
          <Button
            variant="text"
            onClick={showLogs ? () => setShowLogs(false) : loadLogs}
            sx={{ color: "#194AF5", textTransform: "none", pl: 0 }}
          >
            {showLogs ? "Hide Import History" : "View Import History"}
          </Button>

          {showLogs && (
            <Box sx={{ mt: 1, maxHeight: "200px", overflowY: "auto" }}>
              {logsLoading ? (
                <CircularProgress size={20} />
              ) : logs.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No Import History Found!
                </Typography>
              ) : (
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "13px",
                  }}
                >
                  <thead>
                    <tr style={{ background: "#F0F4FF" }}>
                      <th style={{ padding: "8px", textAlign: "left" }}>
                        File Name
                      </th>
                      <th style={{ padding: "8px" }}>Total</th>
                      <th style={{ padding: "8px" }}>Success</th>
                      <th style={{ padding: "8px" }}>Failed</th>
                      <th style={{ padding: "8px" }}>Status</th>
                      <th style={{ padding: "8px", textAlign: "left" }}>
                        Date
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log) => (
                      <tr
                        key={log._id}
                        style={{ borderBottom: "1px solid #f0f0f0" }}
                      >
                        <td style={{ padding: "8px" }}>{log.fileName}</td>
                        <td style={{ padding: "8px", textAlign: "center" }}>
                          {log.totalRecords}
                        </td>
                        <td
                          style={{
                            padding: "8px",
                            textAlign: "center",
                            color: "green",
                          }}
                        >
                          {log.successRecords}
                        </td>
                        <td
                          style={{
                            padding: "8px",
                            textAlign: "center",
                            color: "red",
                          }}
                        >
                          {log.failedRecords}
                        </td>
                        <td
                          style={{
                            padding: "8px",
                            textAlign: "center",
                            color:
                              log.importStatus === "SUCCESS"
                                ? "green"
                                : log.importStatus === "PARTIAL"
                                  ? "orange"
                                  : "red",
                            fontWeight: "bold",
                          }}
                        >
                          {log.importStatus}
                        </td>
                        <td style={{ padding: "8px" }}>
                          {new Date(log.createdAt).toLocaleDateString("en-IN")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Box>
          )}
        </Box>

        {/* Footer */}
        <Grid display="flex" justifyContent="right" sx={{ mt: 3 }}>
          <Button
            sx={{
              bgcolor: "white",
              border: "1px solid #194AF5",
              color: "#194AF5",
              padding: "0.25rem 1.5rem",
              boxShadow: "none",
              ":hover": { bgcolor: "white", boxShadow: "none" },
            }}
            variant="contained"
            onClick={() => {
              onClose();
              setImportResult(null);
              setShowLogs(false);
            }}
          >
            Close
          </Button>
        </Grid>
      </Box>
    </Modal>
  );
};

export default PmjayImportModal;
